declare const I18n;

import React, { useState, useEffect } from "react";
import SelectInput from "@/components/inputs/SelectInput";
import TextInput from "@/components/inputs/TextInput";
import api from "../../../api";

type Props = {
  grades: string[];
  initialLearnerGrade: string;
  initialOrganizationCode: string;
};

const Questionnaire: React.FC<Props> = ({
  grades,
  initialLearnerGrade,
  initialOrganizationCode,
}) => {
  const [grade, setGrade] = useState(initialLearnerGrade);
  const [error, setError] = useState<string | undefined>();
  const [organizationCode, setOrganizationCode] = useState(initialOrganizationCode);
  const [organizationCodeError, setOrganizationCodeError] = useState<string | undefined>();
  const [organizationCodeChanged, setOrganizationCodeChanged] = useState(false)
  const [confirmOrgChange, setConfirmOrgChange] = useState(false)
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false)
  const intialLearnerOrgCode = initialOrganizationCode

  useEffect(() => {
    if (intialLearnerOrgCode !== organizationCode) {
      setOrganizationCodeChanged(true);
    } else {
      setOrganizationCodeChanged(false)
    }
  }, [organizationCode]);

  useEffect(() => {
    const isOrgCodeValid =
      organizationCode.length === 0 || organizationCode.length === 5;

    // Case 1: org code changed but not confirmed
    if (organizationCodeChanged && !confirmOrgChange) {
      setOrganizationCodeError(
        I18n.t(
          "views.annual_questionnaire.learner.messaging.confirm_change_club",
          { original_club_code: intialLearnerOrgCode }
        )
      );
      setSubmitButtonDisabled(true);
      return;
    }

    // Case 2: org code invalid length
    if (!isOrgCodeValid) {
      setOrganizationCodeError(undefined);
      setSubmitButtonDisabled(true);
      setOrganizationCodeError(
        I18n.t(
          "views.annual_questionnaire.learner.messaging.min_club_code_characters",
          { original_club_code: intialLearnerOrgCode }
        )
      );
      return;
    }

    // Case 3: all good
    setOrganizationCodeError(undefined);
    setSubmitButtonDisabled(false);
  }, [organizationCode, organizationCodeChanged, confirmOrgChange]);

  const gradeLabel = (grade: string) => {
    // numeric grades
    if (/^\d+$/.test(grade)) {
      return I18n.t("models.user.grade_labels.grade", { grade });
    }

    // special cases (cegep, post_secondary)
    return I18n.t(`models.user.grade_labels.${grade}`);
  };

  const handleSubmit = async () => {
    if (submitButtonDisabled) return;

    try {
      await api.learnerAnnualQuestionnaireUpdate({
        grade,
        organization_code:
          organizationCode && organizationCode.length === 5 ? organizationCode : "",
      });

      window.location.href = "/dashboard";
    } catch (error) {
      setOrganizationCodeError(
        I18n.t(
          `views.annual_questionnaire.learner.messaging.invalid_org_code`
        )
      );
    }
  };


  return (
    <div style={{ marginTop: "3rem" }}>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SelectInput
          value={grade}
          error={error}
          type="learner"
          field="grade"
          label={I18n.t("models.learner.attributes.grade")}
          className="required"
          columnSize={6}
          onChange={(value) => {
            setGrade(value);
            setError(undefined);
          }}
        >
          {grades.map((gradeSlug) => (
            <option key={gradeSlug} value={gradeSlug}>
              {gradeLabel(gradeSlug)}
            </option>
          ))}
        </SelectInput>

      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}>

        <TextInput
          value={organizationCode}
          error={organizationCodeError}
          type="learner"
          field="organization_code"
          label={I18n.t("views.annual_questionnaire.learner.label.organization_code")}
          helperText={I18n.t("views.annual_questionnaire.learner.messaging.join_new_club")}
          className="required"
          columnSize={6}
          innerStyle={{ paddingBottom: "0rem" }}
          onChange={(value) => {
            setOrganizationCode(value);
            setOrganizationCodeError("");
          }}
        />
      </div>
      {organizationCodeChanged && (
        <div
          className="mt-3"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <div className="col-md-6">
            <div className="form-check d-flex align-items-center">
              <input
                id="confirm-org-change"
                type="checkbox"
                className="form-check-input mt-0"
                checked={confirmOrgChange}
                onChange={(e) => setConfirmOrgChange(e.target.checked)}
              />
              <label
                className="form-check-label ms-2"
                htmlFor="confirm-org-change"
                style={{ marginTop: "1rem", lineHeight: "1.5rem" }}
              >
                {I18n.t(
                  "views.annual_questionnaire.learner.label.confirm_org_change"
                )}
              </label>
            </div>
          </div>
        </div>
      )}
      <div className="mt-4" style={{ display: "flex", justifyContent: "center" }}>
        <button type="submit" disabled={submitButtonDisabled} className="btn btn-primary" onClick={handleSubmit}>
          {I18n.t('views.forms.submit')}
        </button>
      </div>
    </div>
  );
};

export default Questionnaire;
